import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { applyPublicTenant } from '@/utils/publicTenant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, FileCheck2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicEstimatePage = () => {
  const { token } = useParams();
  const [kv, setKv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // 'approved' | 'declined'

  const load = useCallback(async () => {
    setLoading(true);
    applyPublicTenant();
    try {
      const r = await axios.get(`${API}/public/dental/estimate/${token}`);
      setKv(r.data);
      if (['approved', 'declined'].includes(r.data.status)) setDone(r.data.status);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Kostenvoranschlag nicht gefunden.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (approved) => {
    if (!name.trim()) { toast.error('Bitte geben Sie Ihren Namen an.'); return; }
    if (!consent) { toast.error('Bitte bestätigen Sie die Erklärung.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/dental/estimate/${token}/decision`, { approved, name: name.trim(), note: note.trim(), consent: true });
      setDone(approved ? 'approved' : 'declined');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übermittlung fehlgeschlagen'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Lädt…</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600" data-testid="kv-public-error">{error}</div>;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="kv-public-page">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-teal-600 text-white px-6 py-5">
          {kv.practice ? <div className="text-sm opacity-90">{kv.practice}</div> : null}
          <div className="text-xl font-bold flex items-center gap-2"><FileCheck2 size={22} /> Kostenvoranschlag {kv.kv_number}</div>
          <div className="text-sm opacity-90 mt-1">{kv.title}</div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-slate-500">Für: <span className="text-slate-900 font-medium">{kv.customer_name}</span>{kv.valid_until ? <span className="ml-2">· gültig bis {kv.valid_until}</span> : null}</div>

          <div className="divide-y divide-slate-100 border rounded-lg">
            {(kv.line_items || []).map((li, i) => (
              <div key={i} className="flex justify-between px-4 py-2 text-sm" data-testid={`kv-public-line-${i}`}>
                <span className="text-slate-700">{li.service_name}</span>
                <span className="font-medium text-slate-900">CHF {Number(li.total).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between px-4 py-3 bg-slate-50">
              <span className="font-semibold">Geschätztes Total</span>
              <span className="font-bold text-lg text-teal-700" data-testid="kv-public-total">CHF {Number(kv.total).toFixed(2)}</span>
            </div>
          </div>
          {kv.note ? <div className="text-sm text-slate-600 bg-slate-50 rounded-lg px-4 py-3">{kv.note}</div> : null}

          {(kv.installments || []).length > 0 ? (
            <div className="border rounded-lg overflow-hidden" data-testid="kv-public-installments">
              <div className="px-4 py-2 bg-indigo-50 text-indigo-800 text-sm font-semibold">Ratenzahlung</div>
              <div className="divide-y divide-slate-100">
                {kv.installments.map((it, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`kv-public-installment-${i}`}>
                    <span className="text-slate-700 w-16">Rate {i + 1}</span>
                    <span className="text-slate-500 flex-1">fällig {it.due_date}</span>
                    {it.status === 'paid' ? <span className="text-xs text-emerald-600 mr-3">bezahlt</span> : null}
                    <span className="font-medium text-slate-900 tabular-nums">CHF {Number(it.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {kv.installment_summary ? (
                <div className="flex justify-between px-4 py-2 bg-slate-50 text-sm">
                  <span className="text-slate-500">Bereits bezahlt: CHF {Number(kv.installment_summary.paid).toFixed(2)}</span>
                  <span className="font-semibold text-indigo-700">Offen: CHF {Number(kv.installment_summary.open).toFixed(2)}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <p className="text-xs text-slate-400">Dies ist eine unverbindliche Schätzung. Der tatsächliche Aufwand kann je nach Behandlungsverlauf abweichen.</p>

          {done ? (
            <div className={`rounded-xl px-5 py-6 text-center ${done === 'approved' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`} data-testid="kv-public-result">
              {done === 'approved' ? <CheckCircle2 className="mx-auto mb-2" size={36} /> : <XCircle className="mx-auto mb-2" size={36} />}
              <div className="font-semibold">{done === 'approved' ? 'Vielen Dank – Sie haben den Kostenvoranschlag genehmigt.' : 'Sie haben den Kostenvoranschlag abgelehnt.'}</div>
              <div className="text-sm mt-1">Ihre Rückmeldung wurde an die Praxis übermittelt.</div>
            </div>
          ) : (
            <div className="border-t pt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Ihr Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vor- und Nachname" data-testid="kv-public-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Bemerkung (optional)</label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="kv-public-note" />
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="kv-public-consent" />
                <span>Ich habe den Kostenvoranschlag gelesen und bestätige meine Entscheidung mit Angabe meines Namens als digitale Unterschrift.</span>
              </label>
              <div className="flex gap-3 pt-1">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={submitting} onClick={() => decide(true)} data-testid="kv-public-approve"><CheckCircle2 size={18} className="mr-1" /> Genehmigen</Button>
                <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" disabled={submitting} onClick={() => decide(false)} data-testid="kv-public-decline"><XCircle size={18} className="mr-1" /> Ablehnen</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicEstimatePage;
